import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutTemplate,
  Plus,
  Edit3,
  Trash2,
  Copy,
  Clock,
  Shield,
  Shuffle,
  ArrowRight,
  Search,
  Globe,
  FolderOpen,
  BarChart3,
  TrendingUp,
} from 'lucide-react';
import { toast } from '@/utils/toast';
import { templatesApi } from '@/api/endpoints';
import {
  Card,
  Button,
  Badge,
  Input,
  StatCard,
  ConfirmationModal,
} from '@azyntor-labs/nexus-ui';

interface ExamTemplate {
  id: string;
  name: string;
  description: string;
  category?: string;
  tags?: string[];
  is_public?: boolean;
  use_count?: number;
  created_by?: string;
  created_at: string;
  updated_at?: string;
  settings: {
    duration_minutes: number;
    passing_score: number;
    shuffle_questions: boolean;
    shuffle_options: boolean;
    proctoring_enabled: boolean;
    max_attempts: number;
    allow_retake: boolean;
    show_results: boolean;
    allow_review: boolean;
    auto_grade: boolean;
    allow_late_submit: boolean;
    auto_submit_on_timeout: boolean;
    allow_backtracking: boolean;
    require_fullscreen: boolean;
    browser_lockdown: boolean;
    negative_marking: boolean;
    negative_marking_percentage: number;
    certificate_enabled: boolean;
    [key: string]: any;
  };
}

export const ExamTemplatesPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Fetch template stats
  const { data: statsRes } = useQuery({
    queryKey: ['exam-template-stats'],
    queryFn: () => templatesApi.getStats(),
    staleTime: 60000,
  });

  const templateStats = statsRes?.data?.data as {
    total_templates?: number;
    public_templates?: number;
    total_used?: number;
    avg_use_count?: number;
    total_categories?: number;
  } | null;

  // Fetch templates from API
  const { data: templatesData, isLoading, isError } = useQuery({
    queryKey: ['exam-templates'],
    queryFn: async () => {
      const res = await templatesApi.list();
      return res.data;
    },
  });

  const templates: ExamTemplate[] = Array.isArray(templatesData?.data)
    ? templatesData.data
    : Array.isArray(templatesData)
      ? templatesData
      : [];

  // Filter templates by search and category
  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = !searchTerm ||
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const uniqueCategories = Array.from(
    new Set(templates.map((t) => t.category).filter(Boolean))
  ) as string[];

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam-templates'] });
      queryClient.invalidateQueries({ queryKey: ['exam-template-stats'] });
      toast.success('Template deleted');
      setDeleteConfirmId(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete template');
    },
  });

  // Duplicate mutation (create a copy)
  const duplicateMutation = useMutation({
    mutationFn: (template: ExamTemplate) =>
      templatesApi.create({
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        settings: { ...template.settings },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exam-templates'] });
      queryClient.invalidateQueries({ queryKey: ['exam-template-stats'] });
      toast.success('Template duplicated');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to duplicate template');
    },
  });

  const handleUseTemplate = (template: ExamTemplate) => {
    sessionStorage.setItem('exam_template_prefill', JSON.stringify(template.settings));
    navigate('/teacher/exams/create');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Exam Templates</h1>
          <p className="text-sm text-white/50 mt-1">Save and reuse exam configurations</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/teacher/templates/create')}>
          Create Template
        </Button>
      </div>

      {/* Template Stats */}
      {templateStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard
            title="Total Templates"
            value={templateStats.total_templates ?? 0}
            icon={<LayoutTemplate className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Public Templates"
            value={templateStats.public_templates ?? 0}
            icon={<Globe className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Uses"
            value={templateStats.total_used ?? 0}
            icon={<BarChart3 className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Categories"
            value={templateStats.total_categories ?? 0}
            icon={<FolderOpen className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Avg Use Count"
            value={Number(templateStats.avg_use_count ?? 0).toFixed(1)}
            icon={<TrendingUp className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Search and Filter */}
      <Card>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {uniqueCategories.length > 0 && (
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white/90 focus:outline-none focus:ring-1 focus:ring-teal-500/50"
              >
                <option value="">All Categories</option>
                {uniqueCategories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}
            <div className="flex items-center text-sm text-white/50">
              <LayoutTemplate className="w-4 h-4 mr-2" />
              Showing {filteredTemplates.length} of {templates.length} templates
            </div>
          </div>
        </div>
      </Card>

      {/* Loading state */}
      {isLoading && (
        <Card>
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto mb-3" />
            <p className="text-sm text-white/50">Loading templates...</p>
          </div>
        </Card>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <Card>
          <div className="p-12 text-center">
            <LayoutTemplate className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">Failed to load templates</p>
            <p className="text-xs text-white/40 mt-1">Please try again later.</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['exam-templates'] })}
              className="mt-4"
            >
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !isError && templates.length === 0 && (
        <Card>
          <div className="p-12 text-center">
            <LayoutTemplate className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No templates yet</p>
            <p className="text-xs text-white/40 mt-1">Create a template to quickly set up exams with predefined settings</p>
            <Button
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate('/teacher/templates/create')}
              className="mt-4"
              size="sm"
            >
              Create Your First Template
            </Button>
          </div>
        </Card>
      )}

      {/* No search results */}
      {!isLoading && !isError && templates.length > 0 && filteredTemplates.length === 0 && (
        <Card>
          <div className="p-12 text-center">
            <Search className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No templates match your search</p>
            <p className="text-xs text-white/40 mt-1">Try adjusting your search term or category filter</p>
          </div>
        </Card>
      )}

      {/* Templates grid */}
      {!isLoading && !isError && filteredTemplates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id}>
              <div className="p-5 flex flex-col h-full">
                {/* Card header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white/90 truncate">{template.name}</h3>
                    {template.description && (
                      <p className="text-xs text-white/50 mt-1 line-clamp-2">{template.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/teacher/templates/${template.id}/edit`)}
                      title="Edit"
                      className="!p-1.5"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => duplicateMutation.mutate(template)}
                      disabled={duplicateMutation.isPending}
                      title="Duplicate"
                      className="!p-1.5"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(template.id)}
                      title="Delete"
                      className="!p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Category badge */}
                {template.category && (
                  <div className="mb-2">
                    <Badge variant="info" size="sm">{template.category}</Badge>
                  </div>
                )}

                {/* Settings preview */}
                <div className="space-y-2 flex-1">
                  <div className="flex items-center space-x-4 text-xs">
                    <div className="flex items-center space-x-1 text-white/50">
                      <Clock className="w-3 h-3" />
                      <span>{template.settings.duration_minutes}min</span>
                    </div>
                    <div className="text-white/50">
                      Pass: {template.settings.passing_score}%
                    </div>
                    <div className="text-white/50">
                      Attempts: {template.settings.max_attempts}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {template.settings.proctoring_enabled && (
                      <Badge variant="success" size="sm">
                        <span className="flex items-center space-x-1">
                          <Shield className="w-3 h-3" />
                          <span>Proctored</span>
                        </span>
                      </Badge>
                    )}
                    {template.settings.shuffle_questions && (
                      <Badge variant="info" size="sm">
                        <span className="flex items-center space-x-1">
                          <Shuffle className="w-3 h-3" />
                          <span>Shuffle</span>
                        </span>
                      </Badge>
                    )}
                    {template.settings.certificate_enabled && (
                      <Badge variant="secondary" size="sm">Certificate</Badge>
                    )}
                    {template.settings.negative_marking && (
                      <Badge variant="danger" size="sm">Neg. Marking</Badge>
                    )}
                    {template.settings.require_fullscreen && (
                      <Badge variant="warning" size="sm">Fullscreen</Badge>
                    )}
                  </div>

                  {template.use_count != null && (
                    <p className="text-xs text-white/30">
                      Used {template.use_count} time{template.use_count !== 1 ? 's' : ''}
                    </p>
                  )}

                  <p className="text-xs text-white/30">
                    Created {new Date(template.created_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Use Template button */}
                <div className="mt-4">
                  <Button
                    onClick={() => handleUseTemplate(template)}
                    className="w-full"
                    size="sm"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <span>Use Template</span>
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
        title="Delete Template?"
        message="This action cannot be undone. The template will be permanently removed."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
